import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import SectionHeader from '@/components/feature/SectionHeader';
import SearchBar from '@/components/feature/SearchBar';
import WorkCard from '@/components/feature/WorkCard';
import BannerCarousel from '@/pages/home/components/BannerCarousel';
import CategoryEntry from '@/pages/home/components/CategoryEntry';
import { works } from '@/mocks/works';
import { useSeo } from '@/hooks/useSeo';

const HOT_KEYWORDS = ['悬疑', '科幻', '古装', '爱情', '国产剧', '冒险'];

export default function Home() {
  useSeo({
    title: '美丽小蜜桃3影视大全 - 最新电影电视剧资源汇总',
    description:
      '美丽小蜜桃3为您提供丰富的影视内容，包括最新电影、电视剧等多类别资源，方便用户在线查询剧集信息和影视动态。',
    keywords: '美丽小蜜桃3,影视大全,电影资源,电视剧,影视内容,剧情介绍,演员表',
  });

  const featured = works.filter((work) => work.featured);
  const movieLatest = [...works]
    .filter((work) => work.type === 'movie')
    .sort((a, b) => b.year - a.year || b.popularity - a.popularity)
    .slice(0, 6);
  const tvHot = [...works]
    .filter((work) => work.type === 'tv')
    .sort((a, b) => b.popularity - a.popularity)
    .slice(0, 6);

  const movieCount = works.filter((work) => work.type === 'movie').length;
  const tvCount = works.filter((work) => work.type === 'tv').length;

  return (
    <SiteLayout>
      <section className="relative overflow-hidden border-b border-background-200 bg-gradient-to-b from-primary-50 via-background-50 to-background-50">
        <span className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-accent-200/50 blur-3xl" />
        <span className="pointer-events-none absolute -left-20 bottom-0 h-64 w-64 rounded-full bg-primary-200/40 blur-3xl" />
        <div className="container-page relative py-10 md:py-16">
          <div className="flex flex-col items-center text-center">
            <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary-200 bg-background-50 px-4 py-1.5 text-xs text-primary-700 md:text-sm">
              <i className="ri-fire-line text-accent-600" />
              最新影视资讯 · 每日更新
            </span>
            <h1 className="max-w-3xl text-3xl font-bold leading-tight tracking-tight text-foreground-950 md:text-5xl">
              发现值得一看的电影与电视剧
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
              汇聚最新电影、热播剧集的剧情介绍、演员阵容与上映动态，在按下播放键之前，先在这里读懂每一部作品。
            </p>

            <div className="mt-7 w-full max-w-2xl">
              <SearchBar size="lg" />
            </div>

            <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
              <span className="text-xs text-foreground-500 md:text-sm">热门搜索：</span>
              {HOT_KEYWORDS.map((keyword) => (
                <Link
                  key={keyword}
                  to={`/search?q=${encodeURIComponent(keyword)}`}
                  className="cursor-pointer rounded-full border border-background-300 bg-background-50 px-3 py-1 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 md:text-sm"
                >
                  {keyword}
                </Link>
              ))}
            </div>

            <div className="mt-8 grid w-full max-w-2xl grid-cols-3 gap-3 md:gap-6">
              {[
                { value: `${works.length}+`, label: '影视作品' },
                { value: `${movieCount}`, label: '电影收录' },
                { value: `${tvCount}`, label: '电视剧收录' },
              ].map((stat) => (
                <div key={stat.label} className="rounded-lg border border-background-200 bg-background-50 px-3 py-3 md:py-4">
                  <p className="text-lg font-bold text-primary-600 md:text-2xl">{stat.value}</p>
                  <p className="mt-0.5 text-xs text-foreground-500 md:text-sm">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="container-page pt-10 md:pt-14">
        <SectionHeader title="焦点推荐" subtitle="编辑精选，本季最值得一看的影视作品" />
        <BannerCarousel items={featured} />
      </section>

      <section className="container-page pt-12 md:pt-16">
        <SectionHeader title="分类快捷入口" subtitle="按类别快速找到你想看的影视作品" />
        <CategoryEntry movieCount={movieCount} tvCount={tvCount} />
      </section>

      <section className="container-page pt-12 md:pt-16">
        <SectionHeader title="最新上映电影" subtitle="近期上线的电影作品速览" moreTo="/category/movie" />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6 lg:gap-5">
          {movieLatest.map((work) => (
            <WorkCard key={work.id} work={work} />
          ))}
        </div>
      </section>

      <section className="container-page pt-12 md:pt-16">
        <SectionHeader title="热门电视剧" subtitle="正在热播与口碑爆棚的剧集" moreTo="/category/tv" />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6 lg:gap-5">
          {tvHot.map((work) => (
            <WorkCard key={work.id} work={work} />
          ))}
        </div>
      </section>

      <section className="container-page pt-12 md:pt-16">
        <div className="grid grid-cols-1 gap-6 rounded-lg border border-background-200 bg-background-100 p-6 md:grid-cols-3 md:p-10">
          <div className="md:col-span-2">
            <h2 className="text-lg font-bold text-foreground-950 md:text-2xl">关于美丽小蜜桃3影视大全</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">
              美丽小蜜桃3影视大全是一个专注影视资讯展示与查询的平台。我们为喜爱电影和电视剧的用户整理最新影视剧集信息与动态，
              内容涵盖电影与电视剧两大类别，提供详尽的剧情介绍与演员信息，帮助你在观看前快速了解作品背景。
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">
              本站属于资讯展示与信息查询性质，不提供任何播放、下载或交易服务，所有内容均以演示数据形式呈现。
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link
                to="/category/movie"
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                浏览电影
              </Link>
              <Link
                to="/category/tv"
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                浏览电视剧
              </Link>
            </div>
          </div>
          <div className="flex flex-col justify-center gap-4 rounded-lg bg-background-50 p-5">
            {[
              { icon: 'ri-stack-line', title: '分类清晰', desc: '电影 / 电视剧两大类别' },
              { icon: 'ri-search-eye-line', title: '查询便捷', desc: '片名与主演一键搜索' },
              { icon: 'ri-heart-3-line', title: '本地收藏', desc: '喜欢的作品随手收藏' },
            ].map((item) => (
              <div key={item.title} className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                  <i className={`${item.icon} text-lg`} />
                </span>
                <div>
                  <p className="text-sm font-semibold text-foreground-950">{item.title}</p>
                  <p className="mt-0.5 text-xs text-foreground-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}